import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  apiDelete,
  apiPatch,
  applyBulkAllSkippedFeedback,
  companyIdSelector,
  ConfirmResult,
  DialogType,
  DrawerType,
  errors,
  getBulkArchivePath,
  getBulkUpdatePath,
  IApiError,
  IRideBulkUpdatePayload,
  IRydeProcess,
  isHardSoftValidationBulkErrorResponse,
  isSoftValidationBulkErrorResponse,
  Log,
  mapHardValidationErrorToResult,
  normalizeErrors,
  PLURAL_KEY,
  RequestBodyModel,
  RydeProcessTitle,
  RydeUiUnit,
  SimpleResult,
  TRideBulkSoftValidationErrorResponse,
  useDialogs,
  useDrawers,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { usePageContext } from "../contexts";
import { useCrudEditBulk } from "./use-crud-edit-bulk";
import { useSoftValidationModalConfirm } from "./use-soft-validation-modal-confirms";

export const useDynamicFormsBulkActions = (entity: RydeUiUnit) => {
  const { t } = useTranslation();
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setFetching = useSetRecoilState(apiDataFetchingAtom);
  const companyId = useRecoilValue(companyIdSelector);
  const { openDialog } = useDialogs();
  const { confirmSoftValidationAction } = useSoftValidationModalConfirm();
  const { openDrawer } = useDrawers();
  const { handleBulkCrudOk } = useCrudEditBulk(entity);
  const { entity: entityFromContext } = usePageContext();

  const bulkUpdate = useEvent(
    async <T extends RequestBodyModel>(
      ids: number[],
      payload: Partial<T>,
      avoidResetSelectedIds?: boolean,
    ): Promise<SimpleResult<T>> => {
      try {
        setErrors(undefined);
        setFetching(true);
        const requestUrl = getBulkUpdatePath(companyId || 0, entity);
        Log.debug(
          `bulkUpdate ${entity}, company id in selector ${companyId}`,
          requestUrl,
        );
        const apiPayload: IRideBulkUpdatePayload = {
          ids,
          payload: {
            ...payload,
            companyId: companyId,
          },
        };

        let result = await apiPatch<
          SimpleResult<IRydeProcess | TRideBulkSoftValidationErrorResponse>
        >(requestUrl, {
          body: apiPayload,
        });

        /* ride page only specific*/
        if (isSoftValidationBulkErrorResponse(result?.data)) {
          setFetching(false);

          const softValidationResult = await confirmSoftValidationAction(
            result.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return new SimpleResult<T>({ success: false });
          }

          setFetching(true);

          apiPayload.token = result.data.token;
          result = await apiPatch<
            SimpleResult<IRydeProcess | TRideBulkSoftValidationErrorResponse>
          >(requestUrl, { body: apiPayload });
        } else if (isHardSoftValidationBulkErrorResponse(result?.data)) {
          const hardValidationError = result.data.items[0].validationErrors[0];
          setFetching(false);
          const errorResult =
            mapHardValidationErrorToResult(hardValidationError);
          warnAndToastSimpleResult(errorResult, t);
          return new SimpleResult<T>({ success: false });
        }

        if (result?.success) {
          const resultData = result?.data as IRydeProcess;

          handleBulkCrudOk();
          openDrawer<DrawerType.ProcessDrawer>({
            drawerProps: {
              type: DrawerType.ProcessDrawer,
              processId: resultData.id,
              processTitle: RydeProcessTitle.BulkUpdate,
              currentEntityPage: entityFromContext,
              avoidResetSelectedIds,
              entity,
            },
          });
          return new SimpleResult<T>({ success: true });
        }

        if (
          result &&
          applyBulkAllSkippedFeedback({
            result,
            translationFunction: t,
            setErrors,
          })
        ) {
          return new SimpleResult<T>({ success: false, error: result.error });
        }

        return new SimpleResult<T>({ success: false });
      } catch (error) {
        Log.errorAndToast(
          t("errors.bulkFailToUpdateEntities", {
            entity: t(`${entity}.${PLURAL_KEY}`),
          }),
          error,
        );
        const errorsNormalized = normalizeErrors(error as IApiError, t);
        setErrors(errorsNormalized);
        return new SimpleResult<T>({ error: errorsNormalized });
      } finally {
        setFetching(false);
      }
    },
  );

  const bulkArchive = useEvent(async (ids: number[]): Promise<boolean> => {
    try {
      setErrors(undefined);
      setFetching(true);
      const requestUrl = getBulkArchivePath(companyId || 0, entity);
      const result = await apiDelete<SimpleResult<IRydeProcess>>(requestUrl, {
        body: { ids },
      });

      if (result?.success) {
        const resultData = result?.data as IRydeProcess;
        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkArchive,
            entity,
          },
        });
        return true;
      }

      if (
        result &&
        applyBulkAllSkippedFeedback({
          result,
          translationFunction: t,
          setErrors,
        })
      ) {
        return false;
      }

      return false;
    } catch (error) {
      const err = error as { translationKey?: string; metadata?: object };
      if (err?.translationKey === errors.common.bulkArchiveRelationError) {
        openDialog({
          dialogName: DialogType.BulkArchiveRelationErrorDisplayDialog,
          dialogProps: {
            ...err?.metadata,
            entity,
          },
        });
      } else {
        Log.errorAndToast(
          t("errors.bulkFailToArchiveEntities", {
            entity: t(`${entity}.${PLURAL_KEY}`),
          }),
          error,
        );
        setErrors(normalizeErrors(error as IApiError, t));
      }

      return false;
    } finally {
      setFetching(false);
    }
  });

  return {
    bulkUpdate,
    bulkArchive,
  };
};
